import { Controller } from '@hotwired/stimulus'

const STAGE_TITLES = ['Choose background', 'Arrange strip images', 'Add shapes', 'Preview']
const MAX_PAGES = 10
const EFFECT_KEYS = ['wow', 'omg', 'pow', 'cool', 'boom', 'ok', 'lol', 'bang', 'oh', 'no', 'why', 'pop', 'yeah', 'hello', 'bam', 'zap']
const RESIZE_DIRECTIONS = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']
export default class extends Controller {
  static targets = [
    'projectData', 'projectName', 'status', 'backgroundStage', 'composeStage', 'previewStage',
    'shapeTray', 'shapeMenuButton', 'stageTitle', 'backButton', 'nextButton', 'canvasShell', 'canvasLayer',
    'emptyState', 'objectToolbar', 'previewCanvas', 'pageButtons', 'assetList', 'rackSelect',
    'rackSelectGroup', 'noRackMessage', 'exportButton', 'editTextButton', 'addPageButton', 'deletePageButton',
    'libraryModalError'
  ]

  connect () {
    const payload = JSON.parse(this.projectDataTarget.textContent)
    this.projectId = payload.id
    this.updateUrl = payload.update_url
    this.exportUrl = payload.export_url
    this.effectSources = new Map(
      [...this.element.querySelectorAll('[data-effect-key]')].map(button => [button.dataset.effectKey, button.dataset.effectSrc])
    )
    this.pages = (payload.pages || []).map(page => this.normalizePage(page))
    if (this.pages.length === 0) this.pages = [this.blankPage()]
    this.pageIndex = 0
    this.stage = this.currentPage().backgroundKey ? 1 : 0
    this.selectedId = null
    this.hasUnsavedChanges = false
    this.backgroundSources = new Map(
      [...this.element.querySelectorAll('[data-background-key]')].map(button => [button.dataset.backgroundKey, button.dataset.backgroundSrc])
    )
    this.keydownHandler = event => this.handleKeydown(event)
    this.beforeVisitHandler = event => this.warnBeforeVisit(event)
    document.addEventListener('keydown', this.keydownHandler)
    document.addEventListener('turbo:before-visit', this.beforeVisitHandler)
    this.renderPage()
    this.renderStage()
    this.renderPageButtons()
  }

  disconnect () {
    document.removeEventListener('keydown', this.keydownHandler)
    document.removeEventListener('turbo:before-visit', this.beforeVisitHandler)
    this.abortPointerInteraction?.()
    this.abortAssetPointerDrag?.()
    this.removeDragAvatar()
    this.removeDropGhost()
  }

  blankPage () {
    return { id: null, backgroundKey: null, objects: [] }
  }

  normalizePage (page) {
    const data = page.editor_data || {}
    return {
      id: page.id || null,
      backgroundKey: page.background_key || null,
      objects: Array.isArray(data.objects) ? data.objects.map((object, index) => this.normalizeObject(object, index)) : []
    }
  }

  normalizeObject (object, index = 0) {
    return {
      localId: object.localId || object.local_id || this.localId(),
      type: ['image', 'bubble', 'text', 'sticker'].includes(object.type) ? object.type : 'image',
      assetId: object.asset_id || object.assetId || null,
      sourceAttachmentId: object.source_attachment_id || object.sourceAttachmentId || null,
      src: this.staticEffectSourceFor(object) || object.src || null,
      text: String(object.text || '').slice(0, 500),
      x: this.number(object.x, 12),
      y: this.number(object.y, 12),
      width: this.number(object.width, 42),
      height: this.number(object.height, 42),
      rotation: this.number(object.rotation, 0),
      z: this.number(object.z, index + 1)
    }
  }

  staticEffectSourceFor (object) {
    if (object.type !== 'sticker') return null

    const explicitKey = String(object.effect_key || object.effectKey || '').toLowerCase()
    const source = String(object.src || '')
    const effectKey = EFFECT_KEYS.includes(explicitKey)
      ? explicitKey
      : EFFECT_KEYS.find(key => new RegExp(`(?:^|/)${key}(?:-[a-z0-9]+)?\\.png(?:[?#]|$)`, 'i').test(source))
    return this.effectSources?.get(effectKey) || null
  }

  localId () {
    return globalThis.crypto?.randomUUID?.() || `comic-${Date.now()}-${Math.random().toString(16).slice(2)}`
  }

  number (value, fallback) {
    const number = Number(value)
    return Number.isFinite(number) ? number : fallback
  }

  currentPage () { return this.pages[this.pageIndex] }

  chooseBackground (event) {
    const page = this.currentPage()
    page.backgroundKey = event.currentTarget.dataset.backgroundKey
    this.element.querySelectorAll('[data-background-key]').forEach(button => {
      button.classList.toggle('selected', button.dataset.backgroundKey === page.backgroundKey)
    })
    this.renderCanvasBackground()
    this.emptyStateTarget.hidden = true
    this.queueSave()
  }

  async nextStage () {
    if (this.stage === 0 && !this.currentPage().backgroundKey) {
      this.setStatus('Choose a background before continuing.', true)
      return
    }
    if (this.stage === 2) {
      this.nextButtonTarget.disabled = true
      const saved = await this.saveProject()
      this.nextButtonTarget.disabled = false
      if (!saved) return
    }
    if (this.stage < 3) this.stage += 1
    this.renderStage()
    if (this.stage === 3) this.renderPreview()
  }

  previousStage () {
    if (this.stage > 0) this.stage -= 1
    this.renderStage()
  }

  editCurrentPage () {
    this.stage = 2
    this.renderStage()
  }

  renderStage () {
    this.stageTitleTarget.textContent = STAGE_TITLES[this.stage]
    this.backgroundStageTarget.classList.toggle('d-none', this.stage !== 0)
    this.composeStageTarget.classList.toggle('d-none', ![1, 2].includes(this.stage))
    this.shapeMenuButtonTarget.classList.toggle('d-none', this.stage !== 2)
    if (this.stage !== 2) this.closeShapeMenu()
    this.previewStageTarget.classList.toggle('d-none', this.stage !== 3)
    this.backButtonTarget.classList.toggle('d-none', this.stage === 0)
    this.nextButtonTarget.classList.toggle('d-none', this.stage === 3)
    this.nextButtonTarget.textContent = this.stage === 2 ? 'Done' : 'Next'
    if ([1, 2].includes(this.stage)) this.renderPage()
  }

  addAsset (event) {
    if (this.ignoreNextAssetClick) return
    const button = event.currentTarget
    this.addObject({
      type: 'image',
      assetId: Number(button.dataset.assetId),
      sourceAttachmentId: Number(button.dataset.sourceAttachmentId),
      src: button.dataset.assetSrc,
      text: '',
      x: 18,
      y: 18,
      width: 48,
      height: 48
    })
  }

  beginAssetPointerDrag (event) {
    if (event.button !== 0) return
    event.preventDefault()
    const button = event.currentTarget
    const start = { x: event.clientX, y: event.clientY }
    let moved = false
    this.draggedAsset = {
      assetId: Number(button.dataset.assetId),
      sourceAttachmentId: Number(button.dataset.sourceAttachmentId),
      src: button.dataset.assetSrc
    }

    const move = pointer => {
      if (!moved && Math.hypot(pointer.clientX - start.x, pointer.clientY - start.y) < 6) return
      if (!moved) {
        moved = true
        button.classList.add('dragging')
        this.createDragAvatar(pointer.clientX, pointer.clientY, this.draggedAsset.src)
        if (this.hasCanvasShellTarget) this.canvasShellTarget.classList.add('canvas-drop-ready')
      }

      this.updateDragAvatar(pointer.clientX, pointer.clientY)
      const rect = this.canvasLayerTarget.getBoundingClientRect()
      const overCanvas = this.currentPage().backgroundKey && this.pointInsideRect(pointer.clientX, pointer.clientY, rect)
      this.canvasLayerTarget.classList.toggle('asset-drop-active', Boolean(overCanvas))
      this.setDragAvatarOverCanvas(Boolean(overCanvas))

      if (overCanvas) {
        this.updateDropGhost(pointer.clientX, pointer.clientY, rect)
      } else {
        this.removeDropGhost()
      }
    }
    const finish = pointer => {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', finish)
      document.removeEventListener('pointercancel', cancel)
      button.classList.remove('dragging')
      this.canvasLayerTarget.classList.remove('asset-drop-active')
      if (this.hasCanvasShellTarget) this.canvasShellTarget.classList.remove('canvas-drop-ready')
      this.removeDragAvatar()
      this.removeDropGhost()
      if (moved) {
        this.ignoreNextAssetClick = true
        this.placeDraggedAsset(pointer.clientX, pointer.clientY)
        setTimeout(() => { this.ignoreNextAssetClick = false }, 0)
      }
      this.draggedAsset = null
      this.abortAssetPointerDrag = null
    }
    const cancel = () => finish({ clientX: -1, clientY: -1 })
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', finish, { once: true })
    document.addEventListener('pointercancel', cancel, { once: true })
    this.abortAssetPointerDrag = cancel
  }

  createDragAvatar (x, y, src) {
    this.removeDragAvatar()
    const avatar = document.createElement('div')
    avatar.className = 'comic-drag-avatar'
    avatar.style.left = `${x}px`
    avatar.style.top = `${y}px`

    const img = document.createElement('img')
    img.src = src
    img.alt = 'Dragging strip image'
    avatar.appendChild(img)

    const badge = document.createElement('span')
    badge.className = 'comic-drag-badge'
    badge.textContent = 'Drop'
    avatar.appendChild(badge)

    document.body.appendChild(avatar)
    this.dragAvatarElement = avatar
  }

  updateDragAvatar (x, y) {
    if (!this.dragAvatarElement) return
    this.dragAvatarElement.style.left = `${x}px`
    this.dragAvatarElement.style.top = `${y}px`
  }

  setDragAvatarOverCanvas (overCanvas) {
    if (!this.dragAvatarElement) return
    this.dragAvatarElement.classList.toggle('over-canvas', overCanvas)
  }

  removeDragAvatar () {
    if (this.dragAvatarElement) {
      this.dragAvatarElement.remove()
      this.dragAvatarElement = null
    }
  }

  updateDropGhost (clientX, clientY, rect) {
    if (!this.currentPage().backgroundKey || !this.draggedAsset) return
    const width = 42
    const height = 42
    const x = Math.max(0, Math.min(100 - width, (clientX - rect.left) / rect.width * 100 - width / 2))
    const y = Math.max(0, Math.min(100 - height, (clientY - rect.top) / rect.height * 100 - height / 2))

    let ghost = this.canvasLayerTarget.querySelector('.comic-canvas-ghost')
    if (!ghost) {
      ghost = document.createElement('div')
      ghost.className = 'comic-canvas-ghost'

      const img = document.createElement('img')
      img.src = this.draggedAsset.src
      ghost.appendChild(img)

      const hint = document.createElement('span')
      hint.className = 'comic-ghost-hint'
      hint.textContent = 'Place here'
      ghost.appendChild(hint)

      this.canvasLayerTarget.appendChild(ghost)
    }

    ghost.style.left = `${x}%`
    ghost.style.top = `${y}%`
    ghost.style.width = `${width}%`
    ghost.style.height = `${height}%`
  }

  removeDropGhost () {
    const ghost = this.canvasLayerTarget?.querySelector('.comic-canvas-ghost')
    if (ghost) ghost.remove()
  }

  startAssetDrag (event) {
    const button = event.currentTarget
    this.draggedAsset = {
      assetId: Number(button.dataset.assetId),
      sourceAttachmentId: Number(button.dataset.sourceAttachmentId),
      src: button.dataset.assetSrc
    }
    event.dataTransfer.effectAllowed = 'copy'
    event.dataTransfer.setData('text/plain', String(this.draggedAsset.assetId))
    button.classList.add('dragging')
    if (this.hasCanvasShellTarget) this.canvasShellTarget.classList.add('canvas-drop-ready')
  }

  endAssetDrag (event) {
    event.currentTarget.classList.remove('dragging')
    this.canvasLayerTarget.classList.remove('asset-drop-active')
    if (this.hasCanvasShellTarget) this.canvasShellTarget.classList.remove('canvas-drop-ready')
    this.removeDragAvatar()
    this.removeDropGhost()
    this.draggedAsset = null
  }

  showAssetDropTarget (event) {
    if (!this.draggedAsset || !this.currentPage().backgroundKey) return
    event.preventDefault()
    this.canvasLayerTarget.classList.add('asset-drop-active')
  }

  allowAssetDrop (event) {
    if (!this.draggedAsset || !this.currentPage().backgroundKey) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
    const rect = this.canvasLayerTarget.getBoundingClientRect()
    this.updateDropGhost(event.clientX, event.clientY, rect)
  }

  hideAssetDropTarget (event) {
    if (event.currentTarget.contains(event.relatedTarget)) return
    this.canvasLayerTarget.classList.remove('asset-drop-active')
    this.removeDropGhost()
  }

  dropAsset (event) {
    if (!this.draggedAsset || !this.currentPage().backgroundKey) return
    event.preventDefault()
    this.placeDraggedAsset(event.clientX, event.clientY)
    this.canvasLayerTarget.classList.remove('asset-drop-active')
    if (this.hasCanvasShellTarget) this.canvasShellTarget.classList.remove('canvas-drop-ready')
    this.removeDropGhost()
    this.removeDragAvatar()
  }

  placeDraggedAsset (clientX, clientY) {
    if (!this.draggedAsset || !this.currentPage().backgroundKey) return false
    const rect = this.canvasLayerTarget.getBoundingClientRect()
    if (!this.pointInsideRect(clientX, clientY, rect)) return false
    const width = 42
    const height = 42
    const x = Math.max(0, Math.min(100 - width, (clientX - rect.left) / rect.width * 100 - width / 2))
    const y = Math.max(0, Math.min(100 - height, (clientY - rect.top) / rect.height * 100 - height / 2))
    const object = this.addObject({
      type: 'image',
      assetId: this.draggedAsset.assetId,
      sourceAttachmentId: this.draggedAsset.sourceAttachmentId,
      src: this.draggedAsset.src,
      text: '',
      x,
      y,
      width,
      height
    })
    if (object) {
      const element = this.canvasLayerTarget.querySelector(`[data-object-id="${globalThis.CSS.escape(object.localId)}"]`)
      if (element) {
        element.classList.add('just-dropped')
        setTimeout(() => element.classList.remove('just-dropped'), 300)
      }
    }
    return true
  }

  pointInsideRect (x, y, rect) {
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
  }

  async addShape (event) {
    const object = this.addObject({
      type: 'bubble',
      src: event.currentTarget.dataset.shapeSrc,
      text: '',
      x: 22,
      y: 16,
      width: 45,
      height: 24
    })
    this.closeShapeMenu()
    if (object) await this.editObjectText(object)
  }

  async addText () {
    if (!this.currentPage().backgroundKey) {
      this.stage = 0
      this.renderStage()
      return this.setStatus('Choose a background first.', true)
    }
    const page = this.currentPage()
    if (page.objects.length >= 100) return this.setStatus('A page can contain up to 100 objects.', true)

    this.closeShapeMenu()
    const text = await this.promptText('', 'Add Text')
    if (text === null || !text.trim()) return

    this.addObject({
      type: 'text',
      text: text.slice(0, 500),
      x: 24,
      y: 35,
      width: 50,
      height: 14
    })
  }

  addEffect (event) {
    this.addObject({
      type: 'sticker',
      src: event.currentTarget.dataset.effectSrc,
      text: '',
      x: 28,
      y: 28,
      width: 42,
      height: 24
    })
    this.closeShapeMenu()
  }

  toggleShapeMenu () {
    const opening = this.shapeTrayTarget.classList.contains('d-none')
    this.shapeTrayTarget.classList.toggle('d-none', !opening)
    this.shapeMenuButtonTarget.setAttribute('aria-expanded', String(opening))
  }

  closeShapeMenu () {
    this.shapeTrayTarget.classList.add('d-none')
    this.shapeMenuButtonTarget.setAttribute('aria-expanded', 'false')
  }

  addObject (attributes) {
    if (!this.currentPage().backgroundKey) {
      this.stage = 0
      this.renderStage()
      return this.setStatus('Choose a background first.', true)
    }
    const page = this.currentPage()
    if (page.objects.length >= 100) return this.setStatus('A page can contain up to 100 objects.', true)

    const object = this.normalizeObject({ ...attributes, localId: this.localId(), z: page.objects.length + 1 })
    page.objects.push(object)
    if (this.stage === 0 || this.stage === 3) this.stage = ['bubble', 'text', 'sticker'].includes(attributes.type) ? 2 : 1
    this.renderStage()
    this.selectObject(object.localId)
    this.queueSave()
    return object
  }

  renderPage () {
    const page = this.currentPage()
    this.canvasLayerTarget.innerHTML = ''
    this.renderCanvasBackground()
    page.objects.sort((a, b) => a.z - b.z).forEach(object => this.canvasLayerTarget.appendChild(this.objectElement(object)))
    this.emptyStateTarget.hidden = Boolean(page.backgroundKey)
    this.selectedId = null
    this.objectToolbarTarget.hidden = true
    this.highlightSelectedBackground()
  }

  renderCanvasBackground () {
    const source = this.backgroundSources.get(this.currentPage().backgroundKey)
    this.canvasShellTarget.style.backgroundImage = source ? `url("${source}")` : 'none'
  }

  highlightSelectedBackground () {
    const selected = this.currentPage().backgroundKey
    this.element.querySelectorAll('[data-background-key]').forEach(button => button.classList.toggle('selected', button.dataset.backgroundKey === selected))
  }

  objectElement (object) {
    const element = document.createElement('div')
    element.className = `comic-canvas-object comic-object-${object.type}`
    element.dataset.objectId = object.localId
    element.style.left = `${object.x}%`
    element.style.top = `${object.y}%`
    element.style.width = `${object.width}%`
    element.style.height = `${object.height}%`
    element.style.zIndex = object.z
    element.style.transform = `rotate(${object.rotation}deg)`
    element.addEventListener('pointerdown', event => this.beginDrag(event, object.localId))
    element.addEventListener('dblclick', event => this.beginTextEdit(event, object.localId))

    if (object.src) {
      const match = object.src.match(/shape-(\d+)/)
      if (match) {
        element.dataset.shapeNumber = match[1]
        element.classList.add(`comic-shape-${match[1]}`)
      }
      const image = document.createElement('img')
      image.src = object.src
      image.alt = ''
      image.draggable = false
      element.appendChild(image)
    }
    if (object.type === 'bubble' || object.type === 'text') {
      const wrapper = document.createElement('div')
      wrapper.className = 'comic-object-label-wrapper'
      const text = document.createElement('span')
      text.className = 'comic-object-label'
      wrapper.appendChild(text)
      element.appendChild(wrapper)
      this.updateObjectLabel(object, element)
    }

    RESIZE_DIRECTIONS.forEach(direction => {
      const resize = document.createElement('button')
      resize.type = 'button'
      resize.className = 'comic-resize-handle'
      resize.dataset.direction = direction
      resize.title = `Resize ${direction.toUpperCase()}`
      resize.setAttribute('aria-label', resize.title)
      resize.addEventListener('pointerdown', event => this.beginResize(event, object.localId))
      element.appendChild(resize)
    })

    return element
  }

  selectObject (localId) {
    this.selectedId = localId
    this.canvasLayerTarget.querySelectorAll('.comic-canvas-object').forEach(element => {
      element.classList.toggle('selected', element.dataset.objectId === localId)
    })
    this.objectToolbarTarget.hidden = !localId
    const object = this.currentPage().objects.find(item => item.localId === localId)
    this.editTextButtonTarget.hidden = !object || !['bubble', 'text'].includes(object.type)
  }

  clearSelection (event) {
    if (event.target !== this.canvasLayerTarget) return
    this.selectObject(null)
  }

  updateObjectStyle (object) {
    const element = this.canvasLayerTarget.querySelector(`[data-object-id="${globalThis.CSS.escape(object.localId)}"]`)
    if (!element) return
    element.style.left = `${object.x}%`
    element.style.top = `${object.y}%`
    element.style.width = `${object.width}%`
    element.style.height = `${object.height}%`
    element.style.zIndex = object.z
    element.style.transform = `rotate(${object.rotation}deg)`
    if (object.type === 'bubble' || object.type === 'text') {
      this.updateObjectLabel(object, element)
    }
  }

  selectedObject () {
    return this.currentPage().objects.find(object => object.localId === this.selectedId)
  }

  bringToFront () {
    const object = this.selectedObject()
    if (!object) return
    const page = this.currentPage()
    const maxZ = Math.max(...page.objects.map(item => item.z), 0)
    object.z = maxZ + 1
    this.normalizeLayers(page)
    this.renderPage()
    this.selectObject(object.localId)
    this.queueSave()
  }

  sendToBack () {
    const object = this.selectedObject()
    if (!object) return
    const page = this.currentPage()
    const minZ = Math.min(...page.objects.map(item => item.z), 1)
    object.z = Math.max(1, minZ - 1)
    this.normalizeLayers(page)
    this.renderPage()
    this.selectObject(object.localId)
    this.queueSave()
  }

  normalizeLayers (page) {
    page.objects
      .sort((a, b) => a.z - b.z)
      .forEach((object, index) => {
        object.z = index + 1
      })
  }

  beginDrag (event, localId) {
    if (event.target.classList.contains('comic-resize-handle')) return
    this.selectObject(localId)
    const object = this.currentPage().objects.find(item => item.localId === localId)
    if (!object) return

    event.preventDefault()
    const rect = this.canvasLayerTarget.getBoundingClientRect()
    const startX = event.clientX
    const startY = event.clientY
    const origX = object.x
    const origY = object.y

    const move = moveEvent => {
      const deltaX = (moveEvent.clientX - startX) / rect.width * 100
      const deltaY = (moveEvent.clientY - startY) / rect.height * 100
      object.x = Math.max(-object.width + 5, Math.min(100 - 5, origX + deltaX))
      object.y = Math.max(-object.height + 5, Math.min(100 - 5, origY + deltaY))
      this.updateObjectStyle(object)
    }

    this.trackPointer(move)
  }

  beginResize (event, localId) {
    event.preventDefault()
    event.stopPropagation()
    this.selectObject(localId)
    const object = this.currentPage().objects.find(item => item.localId === localId)
    if (!object) return

    const direction = event.currentTarget.dataset.direction
    const rect = this.canvasLayerTarget.getBoundingClientRect()
    const startX = event.clientX
    const startY = event.clientY
    const origX = object.x
    const origY = object.y
    const origW = object.width
    const origH = object.height

    const move = moveEvent => {
      const deltaX = (moveEvent.clientX - startX) / rect.width * 100
      const deltaY = (moveEvent.clientY - startY) / rect.height * 100
      let nextX = origX
      let nextY = origY
      let nextW = origW
      let nextH = origH

      if (direction.includes('e')) nextW = Math.max(8, origW + deltaX)
      if (direction.includes('s')) nextH = Math.max(6, origH + deltaY)
      if (direction.includes('w')) {
        nextW = Math.max(8, origW - deltaX)
        nextX = origX + (origW - nextW)
      }
      if (direction.includes('n')) {
        nextH = Math.max(6, origH - deltaY)
        nextY = origY + (origH - nextH)
      }

      object.x = Math.max(0, Math.min(100 - nextW, nextX))
      object.y = Math.max(0, Math.min(100 - nextH, nextY))
      object.width = Math.min(100, nextW)
      object.height = Math.min(100, nextH)
      this.updateObjectStyle(object)
    }

    this.trackPointer(move)
  }

  trackPointer (move) {
    const finish = () => {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', finish)
      document.removeEventListener('pointercancel', finish)
      this.abortPointerInteraction = null
      this.queueSave()
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', finish, { once: true })
    document.addEventListener('pointercancel', finish, { once: true })
    this.abortPointerInteraction = finish
  }

  async beginTextEdit (event, localId) {
    const object = this.currentPage().objects.find(item => item.localId === localId)
    if (!object || !['bubble', 'text'].includes(object.type)) return
    event.preventDefault()
    event.stopPropagation()
    await this.editObjectText(object, event.currentTarget)
  }

  async editSelectedText () {
    const object = this.selectedObject()
    if (!object || !['bubble', 'text'].includes(object.type)) return this.setStatus('Select a text or speech shape to edit.', true)
    await this.editObjectText(object)
  }

  async promptText (initialValue = '', title = 'Enter Text') {
    const result = await globalThis.Swal.fire({
      customClass: {
        popup: 'swal-popup alert-sm-bg align-items-center justify-content-center w-100',
        title: 'text-white-paint text-black fs-24 mb-2',
        htmlContainer: 'text-black-paint text-white'
      },

      animation: false,
      heightAuto: false,

      title,
      input: 'textarea',
      inputValue: initialValue || '',
      inputPlaceholder: 'Type text here...',
      inputAttributes: {
        maxlength: '500',
        rows: '3',
        autocapitalize: 'off',
        autocorrect: 'off',
      },

      didOpen: (popup) => {
        const textarea = popup.querySelector('.swal2-textarea');

        if (!textarea) return;

        const wrapper = document.createElement('div');
        wrapper.className = 'input-bg bg-center bg-100 bg-no-repeat px-sm-5 px-32 py-3';
        textarea.classList.add('bg-transparent', 'w-100', 'border-0', 'px-sm-2', 'resize-none')
        textarea.parentNode.insertBefore(wrapper, textarea);
        wrapper.appendChild(textarea);
        wrapper.appendChild(overlay);
      },

      showCloseButton: true,
      showCancelButton: true,
      allowOutsideClick: false,
      allowEscapeKey: true,

      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Save',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed || result.value === undefined) return null
    return result.value
  }

  async editObjectText (object, element = null) {
    const value = await this.promptText(object.text, 'Enter Text')
    if (value === null) {
      if (object.type === 'text' && !object.text.trim()) {
        this.removeObject(object.localId)
      }
      return
    }

    const trimmed = value.slice(0, 500)
    if (object.type === 'text' && !trimmed.trim()) {
      this.removeObject(object.localId)
      return
    }

    object.text = trimmed
    element ||= this.canvasLayerTarget.querySelector(`[data-object-id="${globalThis.CSS.escape(object.localId)}"]`)
    if (element) this.updateObjectLabel(object, element)
    this.queueSave()
  }

  updateObjectLabel (object, element) {
    if (!element) return
    const textEl = element.querySelector('.comic-object-label')
    if (!textEl) return

    textEl.textContent = object.text || ''

    const isBubble = object.type === 'bubble'
    const isDoubleBubble = isBubble && (/shape-(6|18)[.-]/.test(object.src || ''))
    const text = (object.text || '').trim()
    const length = text.length
    const width = object.width || 40
    const height = object.height || 20
    const area = width * height * (isDoubleBubble ? 0.65 : 1)

    let lineClamp = 3
    if (height < (isDoubleBubble ? 22 : 18)) lineClamp = 2
    else if (height < (isDoubleBubble ? 32 : 28)) lineClamp = 3
    else if (height < (isDoubleBubble ? 42 : 38)) lineClamp = 4
    else lineClamp = 5

    textEl.style.webkitLineClamp = String(lineClamp)
    textEl.style.lineClamp = String(lineClamp)

    let fontSizeRem
    if (length <= 8) {
      fontSizeRem = Math.min(1.6, Math.max(1.0, Math.sqrt(area) * 0.058))
    } else if (length <= 25) {
      fontSizeRem = Math.min(1.25, Math.max(0.85, Math.sqrt(area) * 0.046))
    } else if (length <= 50) {
      fontSizeRem = Math.min(1.05, Math.max(0.74, Math.sqrt(area) * 0.036))
    } else {
      fontSizeRem = Math.min(0.88, Math.max(0.62, Math.sqrt(area) * 0.028))
    }

    if (!isBubble) fontSizeRem *= 1.15
    if (isDoubleBubble) fontSizeRem *= 0.88
    textEl.style.fontSize = `${fontSizeRem.toFixed(2)}rem`
  }

  rotateLeft () { this.rotateSelected(-15) }
  rotateRight () { this.rotateSelected(15) }

  rotateSelected (amount) {
    const object = this.selectedObject()
    if (!object) return
    object.rotation = (object.rotation + amount) % 360
    this.updateObjectStyle(object)
    this.queueSave()
  }

  removeObject (localId) {
    if (!localId) return
    const page = this.currentPage()
    page.objects = page.objects.filter(object => object.localId !== localId)
    if (this.selectedId === localId) this.selectedId = null
    this.normalizeLayers(page)
    this.renderPage()
    this.queueSave()
  }

  deleteSelected () {
    if (!this.selectedId) return
    this.removeObject(this.selectedId)
  }

  handleKeydown (event) {
    if (((event.key === 'r' || event.key === 'R') && (event.metaKey || event.ctrlKey)) || event.key === 'F5') {
      if (this.hasUnsavedChanges) {
        event.preventDefault()
        this.confirmReload()
        return
      }
    }

    if (event.key === 'Escape') {
      if (this.hasShapeTrayTarget && !this.shapeTrayTarget.classList.contains('d-none')) {
        this.closeShapeMenu()
        return
      }
      if (this.selectedId) {
        this.selectObject(null)
        return
      }
    }

    if (!this.selectedId || !['Delete', 'Backspace'].includes(event.key)) return
    const target = event.target
    if (target instanceof globalThis.Element && target.closest('input, textarea, select, [contenteditable="true"]')) return
    event.preventDefault()
    this.deleteSelected()
  }

  duplicateSelected () {
    const object = this.selectedObject()
    if (!object) return
    this.addObject({ ...object, localId: this.localId(), x: Math.min(object.x + 4, 100 - object.width), y: Math.min(object.y + 4, 100 - object.height) })
  }

  bringForward () { this.moveLayer(1) }
  sendBackward () { this.moveLayer(-1) }

  moveLayer (direction) {
    const page = this.currentPage()
    const sorted = [...page.objects].sort((a, b) => a.z - b.z)
    const index = sorted.findIndex(object => object.localId === this.selectedId)
    const target = index + direction
    if (index < 0 || target < 0 || target >= sorted.length) return
    ;[sorted[index], sorted[target]] = [sorted[target], sorted[index]]
    sorted.forEach((object, position) => { object.z = position + 1 })
    this.renderPage()
    this.selectObject(sorted[target].localId)
    this.queueSave()
  }

  addPage () {
    if (this.pages.length >= MAX_PAGES) return this.setStatus(`A comic can contain up to ${MAX_PAGES} pages.`, true)
    this.pages.push(this.blankPage())
    this.pageIndex = this.pages.length - 1
    this.stage = 0
    this.renderPage()
    this.renderStage()
    this.renderPageButtons()
    this.queueSave()
  }

  async deletePage () {
    if (this.pages.length <= 1) return this.setStatus('A comic must contain at least one page.', true)
    const confirmed = await this.confirmAction({
      title: 'Delete Page?',
      text: `Are you sure you want to delete page ${this.pageIndex + 1}?`,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Delete'
    })
    if (!confirmed) return

    this.pages.splice(this.pageIndex, 1)
    this.pageIndex = Math.min(this.pageIndex, this.pages.length - 1)
    this.stage = this.currentPage().backgroundKey ? 1 : 0
    this.renderPage()
    this.renderStage()
    this.renderPageButtons()
    this.queueSave()
  }

  switchPage (event) {
    this.pageIndex = Number(event.currentTarget.dataset.pageIndex)
    this.stage = this.currentPage().backgroundKey ? 1 : 0
    this.renderPage()
    this.renderStage()
    this.renderPageButtons()
  }

  renderPageButtons () {
    this.pageButtonsTarget.innerHTML = ''

    this.pages.forEach((page, index) => {
      const button = document.createElement('button')
      button.type = 'button'
      button.textContent = index + 1
      button.dataset.pageIndex = index
      button.classList.add('btn', 'w-35px', 'h-35px', 'bg-comic-strip', 'rounded-circle', 'p-0', 'fw-bold', 'border-0', 'text-white', 'scale-hover')

      if (index === this.pageIndex) {
        button.classList.add('active', 'bg-comic-strip-active')
      }

      button.addEventListener('click', event => this.switchPage(event))
      this.pageButtonsTarget.appendChild(button)
    })
    const atPageLimit = this.pages.length >= MAX_PAGES
    this.addPageButtonTarget.disabled = atPageLimit
    this.addPageButtonTarget.title = atPageLimit ? `Maximum ${MAX_PAGES} pages reached` : `Add page (${this.pages.length}/${MAX_PAGES})`
    this.deletePageButtonTarget.disabled = this.pages.length <= 1
    this.deletePageButtonTarget.title = this.pages.length <= 1 ? 'A comic must contain one page' : `Delete page ${this.pageIndex + 1}`
  }

  filterAssets (event) {
    const query = event.currentTarget.value.toLowerCase().trim()
    this.assetListTarget.querySelectorAll('.comic-saved-strip, .comic-saved-asset').forEach(asset => {
      asset.hidden = Boolean(query) && !asset.dataset.search.includes(query)
    })
  }

  queueSave () {
    this.hasUnsavedChanges = true
    this.setStatus('Unsaved changes — click Done to save')
  }

  async saveProject () {
    if (!this.pages.some(page => page.backgroundKey)) {
      this.setStatus('Add a background to at least one page before saving.', true)
      return false
    }
    const removedPages = this.removeBackgroundlessPages()
    if (!this.hasUnsavedChanges) return true
    this.setStatus('Saving…')
    try {
      const response = await fetch(this.updateUrl, {
        method: 'PATCH',
        headers: this.jsonHeaders(),
        body: JSON.stringify({ comic_project: this.serializedProject() })
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(result.errors?.join(', ') || 'Comic could not be saved.')
      result.pages?.forEach((page, index) => { if (this.pages[index]) this.pages[index].id = page.id })
      this.hasUnsavedChanges = false
      this.setStatus(removedPages > 0
        ? `${removedPages} page${removedPages === 1 ? '' : 's'} without a background removed. All changes saved.`
        : 'All changes saved')
      return true
    } catch (error) {
      this.setStatus(error.message, true)
      return false
    }
  }

  removeBackgroundlessPages () {
    const currentPage = this.currentPage()
    const pagesWithBackgrounds = this.pages.filter(page => page.backgroundKey)
    const removedPages = this.pages.length - pagesWithBackgrounds.length
    if (removedPages === 0 || pagesWithBackgrounds.length === 0) return removedPages

    this.pages = pagesWithBackgrounds
    const currentPageIndex = this.pages.indexOf(currentPage)
    this.pageIndex = currentPageIndex >= 0 ? currentPageIndex : Math.min(this.pageIndex, this.pages.length - 1)
    this.hasUnsavedChanges = true
    this.renderPageButtons()
    return removedPages
  }

  async confirmReload () {
    if (this.navigationConfirmationOpen) return
    this.navigationConfirmationOpen = true
    const confirmed = await this.confirmAction({
      title: 'Reload Comic Editor?',
      text: 'You have unsaved comic changes. Reload without saving?',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Reload',
      cancelButtonText: 'Keep Editing'
    })
    this.navigationConfirmationOpen = false
    if (!confirmed) return
    this.hasUnsavedChanges = false
    globalThis.location.reload()
  }

  async warnBeforeVisit (event) {
    if (!this.hasUnsavedChanges) return
    event.preventDefault()
    if (this.navigationConfirmationOpen) return

    this.navigationConfirmationOpen = true
    const confirmed = await this.confirmAction({
      title: 'Leave Comic Editor?',
      text: 'You have unsaved comic changes. Leave without saving?',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Leave',
      cancelButtonText: 'Keep Editing'
    })
    this.navigationConfirmationOpen = false
    if (!confirmed) return

    this.hasUnsavedChanges = false
    const url = event.detail.url
    const action = event.detail.action || 'advance'
    if (globalThis.Turbo?.visit) globalThis.Turbo.visit(url, { action })
    else globalThis.location.href = url
  }

  async confirmAction (options = {}) {
    const result = await globalThis.Swal.fire({
      customClass: {
        popup: 'swal-popup alert-sm-bg align-items-center justify-content-center w-100',
        htmlContainer: 'text-black-paint text-white'
      },
      animation: false,
      heightAuto: false,
      icon: 'warning',
      showCancelButton: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
      ...options
    })
    return result.isConfirmed
  }

  serializedProject () {
    return {
      name: this.projectNameTarget.value.trim(),
      pages: this.pages.map(page => ({
        id: page.id,
        background_key: page.backgroundKey,
        editor_data: {
          version: 1,
          objects: page.objects.map(object => ({
            type: object.type,
            asset_id: object.assetId,
            source_attachment_id: object.sourceAttachmentId,
            src: object.src,
            text: object.text,
            x: object.x,
            y: object.y,
            width: object.width,
            height: object.height,
            rotation: object.rotation,
            z: object.z
          }))
        }
      }))
    }
  }

  jsonHeaders () {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
    }
  }

  async renderPreview () {
    try {
      await this.renderPageToCanvas(this.currentPage(), this.previewCanvasTarget)
    } catch (error) {
      this.setStatus(error.message || 'Preview could not be rendered.', true)
    }
  }

  async renderPageToCanvas (page, canvas) {
    canvas.width = 900
    canvas.height = 900
    const context = canvas.getContext('2d')
    context.clearRect(0, 0, 900, 900)
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, 900, 900)

    const background = this.backgroundSources.get(page.backgroundKey)
    if (background) context.drawImage(await this.loadImage(background), 0, 0, 900, 900)

    for (const object of [...page.objects].sort((a, b) => a.z - b.z)) {
      context.save()
      const x = object.x / 100 * 900
      const y = object.y / 100 * 900
      const width = object.width / 100 * 900
      const height = object.height / 100 * 900
      context.translate(x + width / 2, y + height / 2)
      context.rotate(object.rotation * Math.PI / 180)
      if (object.src) {
        const image = await this.loadImage(object.src)
        context.drawImage(image, -width / 2, -height / 2, width, height)
      }
      if (object.type === 'text' || object.type === 'bubble') {
        this.renderCanvasText(context, object, width, height)
      }
      context.restore()
    }
    return canvas
  }

  renderCanvasText (context, object, width, height) {
    if (!object.text || !object.text.trim()) return

    const isBubble = object.type === 'bubble'
    const isShape6 = isBubble && (/shape-6[.-]/.test(object.src || ''))
    const isShape18 = isBubble && (/shape-18[.-]/.test(object.src || ''))
    const isDoubleBubble = isShape6 || isShape18

    const maxWidth = width * (isDoubleBubble ? 0.54 : isBubble ? 0.68 : 0.90)
    const maxHeight = height * (isDoubleBubble ? 0.40 : isBubble ? 0.60 : 0.88)
    const text = object.text.trim()

    const minFontSize = isDoubleBubble ? 12 : 14
    let optimalFontSize = Math.min(isDoubleBubble ? 36 : 42, Math.max(minFontSize, height * (isDoubleBubble ? 0.22 : isBubble ? 0.28 : 0.40)))
    let bestLines = []
    let bestLineHeight = optimalFontSize * 1.18

    for (let size = optimalFontSize; size >= minFontSize; size -= 1) {
      context.font = `700 ${size}px "Bad Comic", "Comic Sans MS", sans-serif`
      const lineHeight = size * 1.18
      const maxLines = Math.max(1, Math.floor(maxHeight / lineHeight))
      const lines = this.wrapTextLines(context, text, maxWidth, maxLines)
      const totalHeight = lines.length * lineHeight

      if (totalHeight <= maxHeight || size <= minFontSize) {
        optimalFontSize = size
        bestLines = lines
        bestLineHeight = lineHeight
        break
      }
    }

    context.fillStyle = '#111111'
    context.font = `700 ${optimalFontSize}px "Bad Comic", "Comic Sans MS", sans-serif`
    context.textAlign = 'center'
    context.textBaseline = 'middle'

    const totalBlockHeight = (bestLines.length - 1) * bestLineHeight
    const offsetY = isShape6 ? (height * 0.08) : isShape18 ? -(height * 0.09) : 0
    const startY = -(totalBlockHeight / 2) + offsetY

    bestLines.forEach((line, index) => {
      context.fillText(line, 0, startY + index * bestLineHeight, maxWidth)
    })
  }

  wrapTextLines (context, text, maxWidth, maxLines = 4) {
    const paragraphs = String(text || '').split(/\r?\n/)
    const allLines = []
    let truncated = false

    for (let p = 0; p < paragraphs.length; p += 1) {
      const words = paragraphs[p].split(/\s+/)
      let currentLine = ''

      for (let w = 0; w < words.length; w += 1) {
        const word = words[w]
        if (!word) continue

        const testLine = currentLine ? `${currentLine} ${word}` : word
        if (context.measureText(testLine).width <= maxWidth) {
          currentLine = testLine
        } else {
          if (currentLine) {
            allLines.push(currentLine)
            currentLine = ''
            if (allLines.length >= maxLines) {
              truncated = true
              break
            }
          }
          if (context.measureText(word).width > maxWidth) {
            let chunk = ''
            for (const char of word) {
              if (context.measureText(chunk + char).width <= maxWidth) {
                chunk += char
              } else {
                if (chunk) {
                  allLines.push(chunk)
                  chunk = ''
                  if (allLines.length >= maxLines) {
                    truncated = true
                    break
                  }
                }
                chunk = char
              }
            }
            currentLine = chunk
          } else {
            currentLine = word
          }
        }
      }

      if (truncated) break

      if (currentLine) {
        allLines.push(currentLine)
        currentLine = ''
        if (allLines.length >= maxLines && p < paragraphs.length - 1) {
          truncated = true
          break
        }
      }
    }

    if (allLines.length > maxLines) {
      allLines.length = maxLines
      truncated = true
    }

    if (truncated && allLines.length > 0) {
      const lastIndex = allLines.length - 1
      let lastLine = allLines[lastIndex]
      while (lastLine.length > 0 && context.measureText(lastLine + '...').width > maxWidth) {
        lastLine = lastLine.slice(0, -1).trimEnd()
      }
      allLines[lastIndex] = lastLine ? `${lastLine}...` : '...'
    }

    return allLines.length > 0 ? allLines : ['']
  }

  async loadImage (source) {
    if (!source) throw new Error('A comic image source is missing.')

    if (source.startsWith('data:') || source.startsWith('blob:')) {
      return new Promise((resolve, reject) => {
        const image = new globalThis.Image()
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error('A comic image could not be loaded.'))
        image.src = source
      })
    }

    try {
      const response = await fetch(source, { mode: 'cors' })
      if (!response.ok) throw new Error(`HTTP error ${response.status}`)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      return await new Promise((resolve, reject) => {
        const image = new globalThis.Image()
        image.onload = () => resolve(image)
        image.onerror = () => {
          URL.revokeObjectURL(blobUrl)
          reject(new Error('A comic image could not be loaded.'))
        }
        image.src = blobUrl
      })
    } catch (_error) {
      return new Promise((resolve, reject) => {
        const image = new globalThis.Image()
        image.crossOrigin = 'anonymous'
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error('A comic image could not be loaded.'))
        image.src = source
      })
    }
  }

  canvasBlob (canvas) {
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('A comic page could not be rendered.')), 'image/png', 1)
      } catch (error) {
        const message = error.name === 'SecurityError'
          ? 'One of the comic images cannot be exported securely. Remove it and add it again.'
          : 'A comic page could not be rendered.'
        reject(new Error(message))
      }
    })
  }

  openCreateRackModal () {
    const libraryModalEl = document.getElementById('comicLibraryModal')
    const createRackModalEl = document.getElementById('comicCreateRackModal')
    if (libraryModalEl && createRackModalEl) {
      this.clearLibraryModalError()
      const errorEl = document.getElementById('comic_create_rack_error')
      if (errorEl) errorEl.innerHTML = ''
      globalThis.bootstrap?.Modal.getInstance(libraryModalEl)?.hide()
      const createModal = globalThis.bootstrap?.Modal.getOrCreateInstance(createRackModalEl)
      createModal?.show()
    }
  }

  backToLibraryModal () {
    const libraryModalEl = document.getElementById('comicLibraryModal')
    const createRackModalEl = document.getElementById('comicCreateRackModal')
    if (createRackModalEl && libraryModalEl) {
      this.clearLibraryModalError()
      const errorEl = document.getElementById('comic_create_rack_error')
      if (errorEl) errorEl.innerHTML = ''
      globalThis.bootstrap?.Modal.getInstance(createRackModalEl)?.hide()
      const libraryModal = globalThis.bootstrap?.Modal.getOrCreateInstance(libraryModalEl)
      libraryModal?.show()
    }
  }

  showLibraryModalError (message) {
    if (this.hasLibraryModalErrorTarget) {
      this.libraryModalErrorTarget.textContent = message
      this.libraryModalErrorTarget.classList.remove('d-none')
    }
  }

  clearLibraryModalError () {
    if (this.hasLibraryModalErrorTarget) {
      this.libraryModalErrorTarget.textContent = ''
      this.libraryModalErrorTarget.classList.add('d-none')
    }
  }

  async exportToLibrary () {
    this.clearLibraryModalError()
    const rackId = this.rackSelectTarget.value
    if (!rackId) {
      this.showLibraryModalError('Select a Library rack.')
      return
    }

    this.exportButtonTarget.disabled = true
    this.setStatus('Rendering all pages…')
    try {
      if (!await this.saveProject()) {
        this.showLibraryModalError('Please add a background to at least one page before saving.')
        return
      }
      const formData = new FormData()
      formData.append('library_rack_id', rackId)
      for (let index = 0; index < this.pages.length; index += 1) {
        const canvas = document.createElement('canvas')
        await this.renderPageToCanvas(this.pages[index], canvas)
        const blob = await this.canvasBlob(canvas)
        formData.append('pages[]', blob, `comic-page-${index + 1}.png`)
      }
      const response = await fetch(this.exportUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '' },
        body: formData
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(result.errors?.join(', ') || 'Comic could not be added to the Library.')
      this.setStatus(result.message || 'Comic saved to your Library.')
      globalThis.bootstrap?.Modal.getInstance(document.getElementById('comicLibraryModal'))?.hide()
      if (result.library_url) {
        const openLibrary = await this.confirmAction({
          title: 'Comic Saved!',
          text: 'Open the Library rack now?',
          icon: 'success',
          confirmButtonText: 'Open Library',
          cancelButtonText: 'Stay Here'
        })
        if (openLibrary) globalThis.location.href = result.library_url
      }
    } catch (error) {
      this.showLibraryModalError(error.message || 'Comic could not be added to the Library.')
      this.setStatus(error.message, true)
    } finally {
      this.exportButtonTarget.disabled = false
    }
  }

  setStatus (message, error = false) {
    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle('error', error)
  }
}
